import '@fontsource-variable/inter';
import '@fontsource-variable/plus-jakarta-sans';
import { alpha, createTheme, type Theme } from '@mui/material/styles';
import { accentScale, CONSOLE_ACCENT, darkModeAccent, parseHex } from './color';

/**
 * The platform console's design system, adapted from the shop web app's.
 *
 * Look: the same calm canvas, hairline borders, soft layered shadows and 14 px card radii, but with a
 * deep-green (#05473E) accent and a midnight-green sidebar so the console is never mistaken for a shop. Inter
 * for UI text with tabular figures, Plus Jakarta Sans for display headings.
 */

export const fonts = {
  body: '"Inter Variable", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  display: '"Plus Jakarta Sans Variable", "Inter Variable", system-ui, sans-serif',
};

/** Slate-based neutrals. */
export const neutral = {
  0: '#FFFFFF',
  25: '#FCFCFE',
  50: '#F6F7FB',
  100: '#F1F3F7',
  150: '#E9ECF2',
  200: '#DFE3EB',
  300: '#C8CED9',
  400: '#98A2B3',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#1D2939',
  850: '#151D2B',
  900: '#101828',
  950: '#0A0F1A',
} as const;

export const radii = { sm: 8, md: 10, lg: 14, xl: 20 } as const;

const ease = 'cubic-bezier(0.2, 0, 0, 1)';

/** The accent scale (600 = the console green). */
const brandRgb = parseHex(CONSOLE_ACCENT) ?? [5, 71, 62];
export const brand = accentScale(brandRgb);

export function createAppTheme(): Theme {
  const theme = createTheme({
    cssVariables: { colorSchemeSelector: 'class' },
    colorSchemes: {
      light: {
        palette: {
          primary: { main: brand[600], light: brand[400], dark: brand[700], contrastText: '#fff' },
          secondary: { main: '#032B26', contrastText: '#fff' },
          // 700-level greens/oranges: small chip and caption text needs 4.5:1 (WCAG AA).
          success: { main: '#067647', light: '#ECFDF3', dark: '#085D3A' },
          warning: { main: '#B54708', light: '#FFFAEB', dark: '#93370D' },
          error: { main: '#D92D20', light: '#FEF3F2', dark: '#B42318' },
          info: { main: '#1570EF', light: '#EFF8FF', dark: '#175CD3' },
          background: { default: neutral[50], paper: neutral[0] },
          // Secondary and hint text stay readable on tinted surfaces too (≥ 4.5:1).
          text: { primary: neutral[900], secondary: '#5D6679', disabled: '#6B7486' },
          divider: neutral[150],
          grey: { ...neutral },
        },
      },
      dark: {
        palette: {
          // Dark text on the lighter dark-mode accent: white on it falls below 4.5:1.
          primary: { ...darkModeAccent(brandRgb, [17, 26, 24]), contrastText: neutral[950] },
          secondary: { main: neutral[100], contrastText: neutral[900] },
          success: { main: '#47CD89', light: 'rgba(71,205,137,0.12)', dark: '#17B26A' },
          warning: { main: '#FDB022', light: 'rgba(253,176,34,0.12)', dark: '#F79009' },
          error: { main: '#F97066', light: 'rgba(249,112,102,0.12)', dark: '#F04438' },
          info: { main: '#53B1FD', light: 'rgba(83,177,253,0.12)', dark: '#2E90FA' },
          background: { default: '#0A100F', paper: '#111A18' },
          text: { primary: '#F5F7FA', secondary: neutral[400], disabled: '#8A94A6' },
          divider: 'rgba(255,255,255,0.08)',
          grey: { ...neutral },
        },
      },
    },
    shape: { borderRadius: radii.md },
    typography: {
      fontFamily: fonts.body,
      fontSize: 14,
      h1: {
        fontFamily: fonts.display,
        fontWeight: 700,
        fontSize: '2.25rem',
        letterSpacing: '-0.02em',
        lineHeight: 1.15,
      },
      h2: {
        fontFamily: fonts.display,
        fontWeight: 700,
        fontSize: '1.875rem',
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
      },
      h3: {
        fontFamily: fonts.display,
        fontWeight: 700,
        fontSize: '1.5rem',
        letterSpacing: '-0.015em',
        lineHeight: 1.25,
      },
      h4: {
        fontFamily: fonts.display,
        fontWeight: 650,
        fontSize: '1.25rem',
        letterSpacing: '-0.01em',
        lineHeight: 1.3,
      },
      h5: { fontFamily: fonts.display, fontWeight: 650, fontSize: '1.0625rem', lineHeight: 1.4 },
      h6: { fontFamily: fonts.display, fontWeight: 650, fontSize: '0.9375rem', lineHeight: 1.4 },
      subtitle1: { fontWeight: 550, fontSize: '0.9375rem' },
      subtitle2: { fontWeight: 600, fontSize: '0.8125rem' },
      body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', lineHeight: 1.45 },
      overline: { fontSize: '0.6875rem', fontWeight: 650, letterSpacing: '0.08em' },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    },
    transitions: { easing: { easeInOut: ease, easeOut: ease } },
  });

  const v = theme.vars ?? theme;
  const softShadow = '0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)';
  const overlayShadow =
    '0 24px 48px -12px rgba(16,24,40,0.22), 0 8px 16px -4px rgba(16,24,40,0.08)';

  theme.components = {
    // A clear keyboard focus ring on every button, link-button, tab, menu item and list item.
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': {
            outline: `2px solid ${v.palette.primary.main}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Chart series colours (categorical, fixed order; validated for colour-blind separation
        // per mode). Text never wears them.
        ':root': {
          colorScheme: 'light',
          '--viz-1': '#2a78d6',
          '--viz-2': '#eb6834',
          '--viz-3': '#1baf7a',
          '--viz-4': '#eda100',
          '--viz-5': '#e87ba4',
          '--viz-6': '#6250d6',
        },
        '.dark': {
          colorScheme: 'dark',
          '--viz-1': '#3987e5',
          '--viz-2': '#d95926',
          '--viz-3': '#199e70',
          '--viz-4': '#c98500',
          '--viz-5': '#d55181',
          '--viz-6': '#9085e9',
        },
        body: {
          fontFeatureSettings: '"cv11", "ss01", "ss03"',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        '.tabular, td, .MuiTableCell-root': { fontVariantNumeric: 'tabular-nums' },
        '::selection': { background: alpha(brand[500], 0.22) },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            transitionDuration: '0ms !important',
            animationDuration: '0ms !important',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          paddingInline: 16,
          minHeight: 40,
          transition: `background-color 160ms ${ease}, box-shadow 160ms ${ease}, transform 120ms ${ease}`,
          '&:active': { transform: 'translateY(0.5px)' },
        },
        sizeSmall: { minHeight: 32, paddingInline: 12, fontSize: '0.8125rem' },
        sizeLarge: { minHeight: 48, paddingInline: 22, fontSize: '0.9375rem' },
        contained: {
          variants: [
            {
              props: { color: 'primary' },
              style: {
                backgroundImage: `linear-gradient(180deg, ${alpha('#fff', 0.12)}, ${alpha('#fff', 0)})`,
                boxShadow: `${softShadow}, inset 0 0 0 1px ${alpha('#000', 0.06)}`,
                '&:hover': { boxShadow: `0 6px 16px -4px ${alpha(brand[600], 0.45)}` },
              },
            },
          ],
        },
        outlined: {
          borderColor: v.palette.divider,
          backgroundColor: v.palette.background.paper,
          boxShadow: softShadow,
          color: v.palette.text.primary,
          '&:hover': { borderColor: neutral[300], backgroundColor: v.palette.action.hover },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radii.md, transition: `background-color 160ms ${ease}` },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: v.palette.divider },
        rounded: { borderRadius: radii.lg },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          borderRadius: radii.lg,
          borderColor: v.palette.divider,
          boxShadow: softShadow,
          transition: `box-shadow 200ms ${ease}, border-color 200ms ${ease}`,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
    },
    MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          backgroundColor: v.palette.background.paper,
          transition: `box-shadow 160ms ${ease}`,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: v.palette.divider },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: neutral[300] },
          '&.Mui-focused': { boxShadow: `0 0 0 4px ${alpha(brand[500], 0.16)}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
            borderColor: brand[500],
          },
        },
        input: {
          paddingBlock: 10,
          // Browser autofill paints its own yellow/blue box behind saved logins, covering only
          // the text box (not the icon) and clashing with the labels; keep the field's own look.
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px ${v.palette.background.paper} inset`,
            WebkitTextFillColor: v.palette.text.primary,
            caretColor: v.palette.text.primary,
            borderRadius: 0,
            transition: 'background-color 600000s 0s, color 600000s 0s',
          },
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontWeight: 500 } } },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600, fontSize: '0.75rem', height: 24 },
        label: { paddingInline: 10 },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[900],
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: radii.sm,
          padding: '6px 10px',
          boxShadow: overlayShadow,
        },
        arrow: { color: neutral[900] },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radii.lg,
          border: `1px solid ${v.palette.divider}`,
          boxShadow: overlayShadow,
          marginTop: 6,
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { borderRadius: radii.sm, minHeight: 36, fontSize: '0.875rem' } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii.xl,
          boxShadow: overlayShadow,
          border: `1px solid ${v.palette.divider}`,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: alpha(neutral[950], 0.45), backdropFilter: 'blur(4px)' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: v.palette.divider, paddingBlock: 12 },
        head: {
          fontSize: '0.75rem',
          fontWeight: 600,
          color: v.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          backgroundColor: v.palette.background.default,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: `background-color 120ms ${ease}`,
          '&.MuiTableRow-hover:hover': { backgroundColor: v.palette.action.hover },
        },
      },
    },
    MuiListItemButton: { styleOverrides: { root: { borderRadius: radii.md } } },
    MuiAvatar: { styleOverrides: { root: { fontWeight: 650, fontSize: '0.875rem' } } },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: { rounded: { borderRadius: radii.md } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 999, height: 6 }, bar: { borderRadius: 999 } },
    },
    // Standard alerts: a soft tint of the severity colour with readable text in both schemes (MUI's
    // derived colours go near-black on the translucent dark-mode `light` shades).
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          alignItems: 'center',
          variants: (['success', 'info', 'warning', 'error'] as const).map((severity) => ({
            props: { variant: 'standard', severity },
            style: {
              color: v.palette[severity].dark,
              backgroundColor: `color-mix(in srgb, ${v.palette[severity].main} 9%, ${v.palette.background.paper})`,
              border: `1px solid color-mix(in srgb, ${v.palette[severity].main} 22%, transparent)`,
              '& .MuiAlert-icon': { color: v.palette[severity].main },
              ...theme.applyStyles('dark', { color: v.palette[severity].main }),
            },
          })),
        },
      },
    },
    MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44 } } },
  };

  (theme as Theme & { custom: typeof custom }).custom = custom;
  return theme;
}

/** Shadows and surfaces used outside MUI components. */
export const custom = {
  shadow: {
    soft: '0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)',
    raised: '0 12px 24px -6px rgba(16,24,40,0.10), 0 4px 8px -2px rgba(16,24,40,0.05)',
    overlay: '0 24px 48px -12px rgba(16,24,40,0.22), 0 8px 16px -4px rgba(16,24,40,0.08)',
  },
  /** The console sidebar: midnight green in both colour schemes. */
  sidebar: {
    bg: 'linear-gradient(180deg, #06372F 0%, #042B25 55%, #03221D 100%)',
    bgDark: 'linear-gradient(180deg, #04241F 0%, #021814 100%)',
    text: 'rgba(222,247,240,0.74)',
    textStrong: '#FFFFFF',
    muted: 'rgba(190,232,220,0.46)',
    hover: 'rgba(255,255,255,0.06)',
    active: 'rgba(94,201,170,0.18)',
    accent: '#7FD8BE',
    border: 'rgba(255,255,255,0.07)',
  },
  ease,
};
